import { Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { CloseButton, DialogSubTitle, DialogToolbar } from '../../../../shared/dialog/dialog.styles';
import { withPatientIvStatusDialogProvider } from '../../hooks/use-patient-iv-status-dialog/hoc';
import { usePatientIvStatusDialog } from '../../hooks/use-patient-iv-status-dialog/hook';
import { PatientIvStatusDialog } from '../patient-iv-status-dialog';
import { AppointmentEndedMessage } from '../shared/appointment-ended-message/appointment-ended-message';
import { ManuallyVerifiedButton } from '../shared/manually-verified-button';
import { RunVerificationButton } from '../shared/run-verification-button';
import { SelfPayButton } from '../shared/self-pay-button';
import { BoldText, InsuranceErrorList, InsuranceErrorListItem } from '../styles';
import { GetMissingInfoErrorSummary } from './missing-info-error-summary';

const PatientDataMissingStatusDialog: FC = () => {
  const [errorSummary, setErrorSummary] = useState<string[]>([]);
  const { opened, onClose, isLoading, patientInfo, selectedIvSummary, appointment } = usePatientIvStatusDialog();

  useEffect(() => {
    if (opened) {
      if (patientInfo && selectedIvSummary) {
        const insuranceInfo =
          patientInfo.insuranceInfo?.carrierId?.trim().toLocaleLowerCase() ==
          selectedIvSummary?.carrierName?.trim().toLocaleLowerCase()
            ? patientInfo.insuranceInfo
            : patientInfo.secondaryInsuranceInfo?.carrierId == selectedIvSummary?.carrierName
            ? patientInfo.secondaryInsuranceInfo
            : undefined;

        setErrorSummary(GetMissingInfoErrorSummary(patientInfo, selectedIvSummary?.idPayer, insuranceInfo));
      }
    } else {
      setErrorSummary([]);
    }
  }, [opened, selectedIvSummary, patientInfo]);

  return (
    <PatientIvStatusDialog
      opened={opened}
      title="Patient Data Missing"
      subtitle={<BoldText>{`${patientInfo?.firstName} ${patientInfo?.lastName}`}</BoldText>}
    >
      <DialogSubTitle sx={{ margin: '10px 0 20px 0', display: 'block' }}>
        {errorSummary.length > 0 && (
          <>
            The insurance verification process encountered the following issues:
            <InsuranceErrorList>
              {errorSummary.map((error, index) => (
                <InsuranceErrorListItem key={index}>{error}</InsuranceErrorListItem>
              ))}
            </InsuranceErrorList>
            <Typography sx={{ marginBottom: '20px' }}>
              Once corrected in the PMS and the data is synced (within 15 minutes), you'll be able to rerun the
              insurance verification.
            </Typography>
          </>
        )}
        {errorSummary.length === 0 && (
          <>All missing data has been corrected. Please try rerunning the verification again.</>
        )}
      </DialogSubTitle>
      <AppointmentEndedMessage />
      <DialogToolbar>
        <ManuallyVerifiedButton onSuccess={async () => onClose()} disabled={isLoading || appointment?.isEnded} />
        <SelfPayButton onSuccess={async () => onClose()} disabled={isLoading || appointment?.isEnded} />
        <RunVerificationButton
          onSuccess={async () => onClose()}
          disabled={isLoading || errorSummary.length > 0 || appointment?.isEnded}
        />
        <CloseButton onClick={onClose} disabled={isLoading} />
      </DialogToolbar>
    </PatientIvStatusDialog>
  );
};

export default withPatientIvStatusDialogProvider(PatientDataMissingStatusDialog);
